import { Box, Button, Flex, Grid, Text, useDisclosure } from '@chakra-ui/react'
import type {
  InsuranceParamsDto,
  Offer,
  OfferAddon,
  QuoteRating,
} from 'BrokerTsLib/src/dtos'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { usePrintContext } from '@/context/PrintContext'
import { Apps } from '@/dtos'
import { useQueryString } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useUpdateLastWizardStep } from '@/hooks/useWizard'
import { useSaveSelectedAddonInsurance } from '@/hooks/useWizardAddon'
import { useScrollToPageSection } from '@/layouts/components/Sidebar/Sidebar'
import { getQuoteId } from '@/utils/getQuoteId'

import { LoadingOverlay, QuotesFilter, QuotesFilterWrapper } from '@/components'
import { useSetOfferStars } from '@/hooks/useOffer'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { Product } from 'BrokerTsLib/src/enums'
import { CustomWaypoint } from '../../../../components/CustomWaypoint'
import { InsuranceQuoteGroup } from '../../../../components/InsuranceQuoteGroup'
import { Parameters } from '../../../../components/Parameters/Parameters'
import { QuoteItem } from '../../../../components/QuoteItem'
import { useInsuranceParams } from '../../../../context/InsuranceParamsProvider'
import { useOffersAddonContext } from '../../../../context/OffersAddonProvider'
import { useOffersVehicleContext } from '../../../../context/OffersVehicleProvider'
import { useQuotes } from '../../../../context/QuotesContext'
import { useSidebar } from '../../../../context/SidebarContext'
import type { PageSection as Section } from '../../../../layouts/components/Sidebar'
import { ExtendedInsuranceForm } from './forms/ExtendedInsuranceForm'
import { getActiveOffer } from './forms/ExtendedInsuranceForm/ExtendedInsuranceForm'

export const Step2SidebarIds = {
  Parameters: 'parameters',
  Quotes: 'quotes',
} as const

export type Step2SidebarIds = typeof Step2SidebarIds[keyof typeof Step2SidebarIds]

export interface ICalculationState {
  isCalculated: boolean
  isCalculating: boolean
}

export const Step2 = () => {
  const { translate } = useTranslationsContext()
  const navigate = useNavigate()
  const { app, product } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const quotesRef = useRef(null)

  const { printMode } = usePrintContext()
  const { pageSections, setPageSections } = useSidebar()
  const scrollToPageSection = useScrollToPageSection()
  const {
    insuranceParams,
    insuranceParamsId,
    refetchInsuranceParams,
    isDisabled,
  } = useInsuranceParams()
  const ipDto = insuranceParams as InsuranceParamsDto

  const extraAddonsWizard = app == Apps.extra
  const isFleet = app == Apps.fleet
  const isHiddenParameters =
    (extraAddonsWizard && (ipDto.baseParams.addonInsurers?.length ?? 0) == 1) ||
    printMode ||
    isFleet

  const {
    isOpen: isOpenParameters,
    onOpen: onOpenParameters,
    onClose: onCloseParameters,
    onToggle: onToggleParameters,
  } = useDisclosure({ defaultIsOpen: !insuranceParams?.generalParams.existsOffers })

  const {
    refetchOffers: getOffers,
    offers,
    updateOffer,
    setOfferIncluded,
    showAll,
    setShowAll: changeShowAll,
    filtered: baseFiltered,
    isLoading,
    calculatingOffersIds
  } = useOffersVehicleContext()
  const {
    refetchOffers: getAddonOffers,
    offers: addonOffers,
    isLoading: isLoadingAddonOffers,
    calculatingOffersIds: calculatingAddonOffersIds,
  } = useOffersAddonContext()

  const filtered = useMemo(() => {
    return [
      ...baseFiltered.filter(x => x.offerId == insuranceParams?.generalParams.selectedOfferId),
      // vybrana ma byt vzdy na prvnim miste
      ...baseFiltered.filter(x => x.offerId != insuranceParams?.generalParams.selectedOfferId)
    ].filter((x) => !printMode || (x.errors.filter((e) => !e.softError).length == 0 && x.price > 0))
  }, [printMode, baseFiltered, insuranceParams?.generalParams.selectedOfferId])

  const {
    setShowFilter,
    onCalculationStateChange,
    calculationState,
    setCalculationState,
  } = useQuotes()
  const [addonsCalculationState, setAddonsCalculationState] = useState<{
    isCalculated: boolean
    isCalculating: boolean
  }>({ isCalculated: false, isCalculating: false })

  const { setWrapperMarginTop } = useSidebar()
  const [highlightedQuotesList, setHighlightedQuotesList] = useState<Offer[]>([])
  const [ratingChanged, setRatingChanged] = useState(new Date())

  const isCalculatingMainOfferAny =
    isLoading || (calculatingOffersIds?.length ?? 0) > 0
  const isCalculatingAddonOfferAny =
    isLoadingAddonOffers || (calculatingAddonOffersIds?.length ?? 0) > 0
  const query = useQueryString()
  const [searchOfferId, setSearchOfferId] = useState<string | null>(
    query.get('offer'),
  )
  const [shouldScrollToQuotes, setShouldScrollToQuotes] = useState(false)

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()

  // submit vybranych doplnku v samostatnem addon wizardu
  const {
    mutateAsync: saveSelectedAddonInsurance,
    isPending: isLoadingSaveSelectedAddonInsurance,
  } = useSaveSelectedAddonInsurance()

  const saveSelectedAddons = () => {
    saveSelectedAddonInsurance(Number(insuranceParamsId)).then(() => {
      updateStep({ paramsId: Number(insuranceParamsId), step: 3 }).then(() => {
        refetchInsuranceParams().then(() => {
          navigate(`/extra/steps/3/${insuranceParamsId}`)
        })
      })
    })
  }

  // scroll na konkretni offeru, pokud je predana v url
  useEffect(() => {
    if (
      !!searchOfferId &&
      filtered.findIndex((x) => x.offerId == parseInt(searchOfferId)) >= 0 &&
      calculationState.isCalculated &&
      !calculationState.isCalculating
    )
      setTimeout(() => {
        scrollToPageSection(getQuoteId(searchOfferId)) // nahoru
        // scrollToOffer(searchOfferId) // na stred
        setSearchOfferId(null)
      }, 100)
  }, [searchOfferId, filtered, calculationState])

  const handleState = useCallback(
    (isCalculated: boolean, isCalculating: boolean) => {
      setShowFilter(isCalculated)
      setCalculationState({ isCalculated, isCalculating })
      onCalculationStateChange?.({ isCalculated, isCalculating })
    },
    [],
  )

  const handleAddonsState = useCallback(
    (isCalculated: boolean, isCalculating: boolean) => {
      setAddonsCalculationState({ isCalculated, isCalculating })
    },
    [],
  )
  // nacteni existujicich offer na pageLoad
  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    if (firstLoad) setFirstLoad(false)
    else return

    let scroll = false
    // doplnkovy wizard nema klasicke offery
    if (!extraAddonsWizard && insuranceParams?.generalParams.existsOffers) {
      handleState(false, true)
      getOffers({})
      scroll = true
    }

    // offery ziskavam na page load pouze pokud existuji nejake
    // pro subaru nejsou doplnky
    if (insuranceParams?.generalParams.existsOffers || extraAddonsWizard) {
      handleAddonsState(false, true)
      getAddonOffers({})
        .then(refetchInsuranceParams) // Refreshnu paramsy, protože getAddonOffers může vytvoří varianty a offery.

      scroll = true
    }

    if (scroll) {
      setTimeout(() => {
        scrollToQuotes()
      }, 1000)
    }
  }, [
    !insuranceParams?.generalParams.existsOffers,
    firstLoad
  ])

  // pri scrollu na offers jenom nastavim priznak a o samotny scroll se postara useEffect
  const scrollToQuotes = () => {
    setShouldScrollToQuotes(true)
  }

  // scrollovani na kalkulace
  useEffect(() => {
    // Chceme scroll až po zobrazení sekce nabídek
    if (
      shouldScrollToQuotes &&
      insuranceParams?.generalParams.existsOffers &&
      (calculationState.isCalculating || calculationState.isCalculated)
    ) {
      // S timeoutem, aby se stihly dokončit animace a změny layoutu.
      setTimeout(() => scrollToPageSection(Step2SidebarIds.Quotes), 500)
      setShouldScrollToQuotes(false)
    }
  }, [
    shouldScrollToQuotes,
    insuranceParams?.generalParams.existsOffers,
    calculationState.isCalculating,
    calculationState.isCalculated,
    filtered.length,
  ])

  // automaticka zmena stavu na zaklade dat
  useEffect(() => {
    // do offers se dostane az po kalkulaci, nebo kdyz je nabidka uz sjednana
    handleState(offers.length > 0, isCalculatingMainOfferAny)
  }, [offers.length, isCalculatingMainOfferAny])

  // automaticka zmena stavu na zaklade dat
  useEffect(() => {
    // do offers se dostane az po kalkulaci, nebo kdyz je nabidka uz sjednana
    handleAddonsState(addonOffers.length > 0 || extraAddonsWizard, isCalculatingAddonOfferAny)
  }, [addonOffers.length, isCalculatingAddonOfferAny])

  // ziskanni / zajisteni vytvoreni offers
  const handleCalculateOffers = () => {
    onCloseParameters()
    scrollToQuotes()

    getAddonOffers({ force: true })

    return getOffers({ force: true }).then(() => {
      setShowFilter(true)

      // aby se pripadne nove varianty zobrazily ve filteru a aby paramsy uz na sobe mely existsOffers
      if (!insuranceParams?.generalParams.existsOffers) refetchInsuranceParams()
    })
  }

  const handleAddNewVariant = () => {
    onOpenParameters()
    scrollToPageSection(Step2SidebarIds.Parameters)
  }

  const { mutateAsync: saveOfferStars } = useSetOfferStars()

  const handleRatingChange = useCallback(
    (quoteId: number, rating: QuoteRating) => {
      const offer = filtered.find((x) => x.offerId == quoteId)!
      offer.stars = rating
      updateOffer(offer)
      setRatingChanged(new Date())
      saveOfferStars({ offerId: quoteId, star: rating ?? 0 })
    },
    [filtered],
  )

  useEffect(() => {
    const newHightlightedQuotes = filtered.filter(
      (quote) =>
        quote.stars! > 0 ||
        quote.offerId == insuranceParams?.generalParams.selectedOfferId,
    )

    setHighlightedQuotesList(newHightlightedQuotes)
  }, [filtered, ratingChanged, insuranceParams?.generalParams.selectedOfferId])

  useEffect(() => {
    if (calculationState.isCalculated) {
      setTimeout(() => {
        const quotesSection = document.getElementById(Step2SidebarIds.Quotes)
        const marginTop = quotesSection?.getBoundingClientRect().top

        if (marginTop)
          setWrapperMarginTop?.(marginTop)
      }, 100)
    }
  }, [calculationState.isCalculated, quotesRef, setWrapperMarginTop])

  // aktivni doplnkove offery
  const activeAddonOffers = useMemo(() => {
    const result = addonOffers.reduce(
      (acc, offer) => {
        const existingGroup = acc.find(
          (group) =>
            group.insurerId === offer.insurerId &&
            group.riskVariantId === offer.riskVariantId,
        )

        if (existingGroup) {
          existingGroup.offers.push(offer)
        } else {
          acc.push({
            insurerId: offer.insurerId,
            riskVariantId: offer.riskVariantId,
            offers: [offer],
          })
        }

        return acc
      },
      [] as {
        insurerId: number
        riskVariantId: number
        offers: OfferAddon[]
      }[],
    )

    return result.map((group) => {
      return getActiveOffer(group.offers)
    })
  }, [addonOffers])

  const isCalculatingAny = useMemo(
    () =>
      calculationState.isCalculating || addonsCalculationState.isCalculating,
    [calculationState.isCalculating, addonsCalculationState.isCalculating],
  )

  const validSelected = useMemo(() => {
    return activeAddonOffers.filter(
      (x) =>
        x.errors.findIndex((e) => !e.softError) < 0 &&
        (ipDto.baseParams.addonVariants?.findIndex(
          (av) => av.riskVariantId == x.riskVariantId && av.selected,
        ) ?? 0) >= 0,
    )
  }, [activeAddonOffers.length, ipDto])

  const [anyValidSelected, setAnyValidSelected] = useState(
    validSelected.length > 0,
  )
  useEffect(() => {
    if (validSelected.length > 0 != anyValidSelected)
      setAnyValidSelected(validSelected.length > 0)
  }, [validSelected])

  const [isFilterOpen, setIsFilterOpen] = useState(false)

  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (!isHiddenParameters) {
      array.push({
        id: Step2SidebarIds.Parameters,
        sectionTitle: '',
        index: i++,
        children: (
          <CustomWaypoint sectionId={Step2SidebarIds.Parameters}>
            <Box>
              <Parameters
                isCollapsed={!isOpenParameters}
                id={Step2SidebarIds.Parameters}
                cardHeaderProps={{
                  cursor: 'pointer',
                  onClick: onToggleParameters,
                }}
                calculationState={
                  extraAddonsWizard ? addonsCalculationState : calculationState
                }
                onCalculateOffers={handleCalculateOffers}
              />
            </Box>
          </CustomWaypoint>
        ),
      })
    }

    if (insuranceParams?.generalParams.existsOffers || isHiddenParameters)
      array.push({
        id: Step2SidebarIds.Quotes,
        sectionTitle: '',
        isDisabled:
          !calculationState.isCalculated &&
          !addonsCalculationState.isCalculated,
        index: i++,
        children: (
          <>
            <CustomWaypoint sectionId={Step2SidebarIds.Quotes}>
              <Grid sx={{ gap: 8, position: 'relative' }}>
                <QuotesFilterWrapper
                  sx={{
                    display: { base: 'none', md: 'block' },
                    position: 'absolute',
                    left: '-282px',
                    width: '250px',
                    top: '96px',
                    zIndex: 1
                  }}>
                  <QuotesFilter
                    showFilter={isFilterOpen}
                    onCloseClick={() => setIsFilterOpen(false)}
                  />
                </QuotesFilterWrapper>
                <InsuranceQuoteGroup
                  id={Step2SidebarIds.Quotes}
                  ref={quotesRef}
                  showHeader={!printMode}
                  showFooter={!printMode}
                  highlightedQuotesList={printMode || product == Product.VehicleLeasingInsurance || offers.length == 1 || isPublicPoleas ? [] : highlightedQuotesList}
                  sx={{
                    minHeight: 'calc(100vh - 170px)',
                    alignContent: 'baseline',
                  }}
                  groupHeaderProps={{
                    visibleCount: extraAddonsWizard
                      ? undefined
                      : filtered.length,
                    totalCount: extraAddonsWizard ? undefined : offers.length,
                    enableAddVariant: !isDisabled && !extraAddonsWizard && !isFleet,
                    enableSortByPrice: !extraAddonsWizard,
                    enableSortByInsurer: !extraAddonsWizard,
                    enableShowAll: !extraAddonsWizard,
                    sectionTitle: isCalculatingAny
                      ? translate('calculating', 'Kalkuluji') + '...'
                      : translate('wizard.step2.calculations', 'Kalkulace pojištění'),
                    onShowAllQuotes: () => changeShowAll(!showAll),
                    onAddNewVariant: () => {
                      handleAddNewVariant()
                    },
                    calculationState: {
                      isCalculated:
                        calculationState.isCalculated ||
                        addonsCalculationState.isCalculated,
                      isCalculating: isCalculatingAny,
                    },
                  }}
                  groupFooterProps={
                    !calculationState?.isCalculated
                      ? undefined
                      : {
                        enableAddVariant: !isDisabled && !extraAddonsWizard && !isFleet,
                        onAddNewVariant: () => {
                          handleAddNewVariant()
                        },
                      }
                  }
                  customFooter={
                    extraAddonsWizard && anyValidSelected ? (
                      <Flex justifyContent="center">
                        <Button
                          colorScheme="green"
                          onClick={saveSelectedAddons}
                          isLoading={isLoadingSaveSelectedAddonInsurance}
                        >
                          {translate('wizard.step2.contractSelected', 'Sjednat vybrané')}
                        </Button>
                      </Flex>
                    ) : undefined
                  }
                  extraOffersNode={
                    activeAddonOffers.length > 0 && (
                      <Grid
                        sx={{
                          w: '100%',
                          display: 'grid',
                          gridTemplateColumns: {
                            base: '1fr',
                            lg: extraAddonsWizard ? '1fr' : '1fr 1fr',
                          },
                          alignItems: 'start',
                          gap: 8,
                        }}
                      >
                        {activeAddonOffers
                          .filter(
                            (x) =>
                              !printMode ||
                              (x.errors.filter((e) => !e.softError).length ==
                                0 &&
                                x.price > 0),
                          )
                          ?.sort((a, b) => a.insurerId - b.insurerId)
                          .map((x) => {
                            const selected =
                              ipDto.baseParams.addonVariants?.find(
                                (av) => av.riskVariantId == x?.riskVariantId,
                              )?.selected ?? false
                            const hardErrors = x.errors.filter(
                              (e) => !e.softError,
                            )
                            return (
                              <LoadingOverlay key={x.offerId}>
                                <ExtendedInsuranceForm
                                  defaultSelected={
                                    selected && hardErrors.length == 0
                                  }
                                  defaultOpen={selected && hardErrors.length == 0}
                                  onChange={(selected) => {
                                    if (!selected) {
                                      // existuje jina validni vybrana
                                      setAnyValidSelected(
                                        validSelected.findIndex(
                                          (vs) => vs.offerId != x.offerId,
                                        ) >= 0,
                                      )
                                    } else {
                                      // nesmi byt errorova
                                      setAnyValidSelected(hardErrors.length == 0)
                                    }
                                  }}
                                  key={x.offerId}
                                  offer={x}
                                  spacing={8}
                                />
                              </LoadingOverlay>
                            )
                          })}
                      </Grid>
                    )
                  }
                >
                  {(calculationState.isCalculated &&
                    !calculationState.isCalculating &&
                    filtered.length == 0 &&
                    addonsCalculationState.isCalculated &&
                    !addonsCalculationState.isCalculating &&
                    addonOffers.length == 0) ||
                    (
                      extraAddonsWizard &&
                      addonsCalculationState.isCalculated &&
                      !addonsCalculationState.isCalculating &&
                      addonOffers.length == 0
                    ) && (
                      <Text fontWeight="bold" textAlign={'center'}>
                        {translate('wizard.step2.noOffers', 'Pro vybrané kritéria nebyly zkalkulováný žádné nabídky')}
                      </Text>
                    )}

                  {calculationState.isCalculated &&
                    filtered &&
                    filtered.length > 0 &&
                    filtered
                      .map((quote, index) => {
                        return (
                          <LoadingOverlay key={quote.offerId}>
                            <QuoteItem
                              showTour={isPublicPoleas && index == 0 && !isCalculatingAny}
                              offerId={quote.offerId}
                              isSelected={
                                insuranceParams?.generalParams.selectedOfferId == quote.offerId
                              }
                              onRatingChange={handleRatingChange}
                              onIncludedChange={setOfferIncluded}
                            />
                          </LoadingOverlay>
                        )
                      })}
                </InsuranceQuoteGroup>
              </Grid>
            </CustomWaypoint>
          </>
        ),
      })

    setPageSections(array)
  }, [
    isHiddenParameters,
    extraAddonsWizard,
    calculationState.isCalculating,
    calculationState.isCalculated,
    addonsCalculationState.isCalculating,
    addonsCalculationState.isCalculated,
    filtered,
    activeAddonOffers,
    insuranceParams?.generalParams.existsOffers,
    isOpenParameters,
    addonOffers,
    validSelected,
    highlightedQuotesList,
    isPublicPoleas
  ])

  return (

    <Grid sx={{ gap: 8 }}>
      {pageSections.map((x) => (
        <React.Fragment key={x.id}>{x.children}</React.Fragment>
      ))}
    </Grid>
  )
}
